/**
 * Created by fris4874
 *
 * */

/**
 * React Modules
 */
import {
  call, put, takeEvery, select
} from 'redux-saga/effects';
/**
 * Custom Modules
 */
import segmentDataApi from '../../externals/segmentDataProxy';
import {
  UNABLE_TO_LOAD_SEGMENT_CODES,
  UNABLE_TO_LOAD_SEGMENT_DETAILS,
  UNABLE_TO_SAVE_SEGMENT_DETAILS,
  UNABLE_TO_DELETE_SEGMENT_DETAILS,
  UNABLE_TO_LOAD_SEGMENT_VENDOR_MAPPING,
  SEGMENT_ALREADY_DELETED
} from '../../helpers/messageConstant';
import {
  FETCH_SEGMENT_CODES,
  receiveSegmentCodes,
  FETCH_SEGMENT_DETAILS,
  receiveSegmentdetails,
  SAVE_SEGMENT_DETAILS,
  receiveSegmentDetailsPersistStatus,
  UPDATE_SEGMENT_DETAILS,
  receiveSegmentDetailsUpdatetStatus,
  DELETE_SEGMENT_DETAILS,
  receiveSegmentDetailsDeleteStatus,
  FETCH_SEGMENT_VERSIONS,
  receiveSegmentVersions,
  segmentVersionsError,
  FETCH_SEGMENT_VENDOR_MAPPING,
  receiveSegmentVendorMapping,
  resetSelectedVersion,
  resetSegmentDetailsAndComments,
  setSelectedSegmentCode
} from '../actions/segmentDataActions';
import {
  receiveErrorInSegmentCodesFetch,
  receiveErrorSegmentDetailsFetch,
  receiveErrorInSegmentDetailsCreation,
  receiveErrorInSegmentDetailsUpdate,
  receiveErrorInSegmentDetailsDeletion,
  receiveErrorInSegmentDetailsFetchDeleted,
} from '../actions/errorDialogActions';
import { syncSelectedVendorInFilter } from '../actions/vendorActions';
import { SUCCESS_EVENTS } from '../../helpers/constants';

/**
 * Worker Saga: will be fired on segment details related actions
 */
export function* getSegmentCodes({ data }) {
  const { segmentType, vendor } = data;
  try {
    const segmentCodes = yield call(segmentDataApi.getSegmentCodes, segmentType, vendor);
    yield put(receiveSegmentCodes({ segmentCodes, vendor }));
  } catch (e) {
    yield put(receiveErrorInSegmentCodesFetch(UNABLE_TO_LOAD_SEGMENT_CODES));
  }
}

export function* fetchSegmentVendorMapping({ data }) {
  const {
    vendors, status, effectiveDate, includeAllVendors
  } = data;

  try {
    const segmentVendorMapping = yield call(
      segmentDataApi.getSegmentVendorMapping,
      vendors,
      status,
      effectiveDate,
      includeAllVendors
    );
    yield put(receiveSegmentVendorMapping(segmentVendorMapping));
  } catch (e) {
    yield put(receiveErrorInSegmentCodesFetch(UNABLE_TO_LOAD_SEGMENT_VENDOR_MAPPING));
  }
}

function* handleDeletedSegmentFlow(segmentType, segmentCode, selectedVendorCode) {
  yield put(receiveErrorInSegmentDetailsFetchDeleted(SEGMENT_ALREADY_DELETED));
  yield put(resetSelectedVersion());
  yield put(resetSegmentDetailsAndComments());

  let segmentCodes = [];

  try {
    const segmentCodeResponse = yield call(
      segmentDataApi.getSegmentCodes,
      segmentType,
      selectedVendorCode
    );

    segmentCodes = Array.isArray(segmentCodeResponse) ? segmentCodeResponse : [];

    yield put(receiveSegmentCodes({ segmentCodes, vendor: selectedVendorCode }));
  } catch (e) {
    // silent
  }

  if (Array.isArray(segmentCodes) && segmentCodes.includes(segmentCode)) {
    yield put(setSelectedSegmentCode(segmentCode));

    try {
      const versionData = yield call(
        segmentDataApi.getSegmentVersions,
        segmentType,
        segmentCode
      );
      yield put(receiveSegmentVersions(versionData));
    } catch (e) {
      // silent
    }
  }
}

export function* fetchSegmentDetails({ data }) {
  const { segmentType, segmentCode, segmentVersion } = data;
  const selectedVendorCode = yield select(
    (state) => state.segmentDataReducer.selectedVendorCode
  );
  try {
    const segmentDetails = yield call(segmentDataApi.getSegmentDetails, segmentType, segmentCode, segmentVersion);
    yield put(receiveSegmentdetails({ segmentDetails, segmentCode }));
  } catch (e) {
    if (e?.response?.data === SEGMENT_ALREADY_DELETED) {
      yield call(handleDeletedSegmentFlow, segmentType, segmentCode, selectedVendorCode);
    } else {
      yield put(receiveErrorSegmentDetailsFetch(UNABLE_TO_LOAD_SEGMENT_DETAILS));
    }
  }
}

export function* saveSegmentDetail({ data, isNewSegment }) {
  try {
    const response = yield call(segmentDataApi.createSegmentDetail, data, isNewSegment);

    if (isNewSegment && response?.version) {
      const { segmentCode, segmentType, suvcs } = data;
      const firstVendor = suvcs[0]?.vendors?.[0];
      const versionData = {
        future: response.version
      };

      try {
        const segmentCodes = yield call(segmentDataApi.getSegmentCodes, segmentType.toLowerCase(), firstVendor);
        yield put(receiveSegmentCodes({ segmentCodes, vendor: firstVendor }));
      } catch (fetchError) {
        // silent
      }

      try {
        const createdSegmentDetails = yield call(segmentDataApi.getSegmentDetails, segmentType.toLowerCase(), segmentCode, response.version);
        yield put(receiveSegmentdetails({
          segmentDetails: createdSegmentDetails,
          segmentCode,
          eventType: SUCCESS_EVENTS.FETCH_NEWLY_CREATED_SEGMENT_DETAILS,
          selectedFirstVendor: firstVendor
        }));
        yield put(receiveSegmentVersions(versionData));
        yield put(setSelectedSegmentCode(segmentCode));
      } catch (fetchError) {
        // silent
      }
    }
    yield put(receiveSegmentDetailsPersistStatus(response));
  } catch (e) {
    const errorMessage = e.response?.data ? e.response?.data : UNABLE_TO_SAVE_SEGMENT_DETAILS;
    yield put(receiveErrorInSegmentDetailsCreation(errorMessage));
  }
}

export function* updateSegmentDetail({ data }) {
  const {
    selectedVendorCode,
    segmentType,
    segmentCode,
    segmentDetails
  } = data;

  try {
    const response = yield call(segmentDataApi.createSegmentDetail, segmentDetails, false);

    try {
      const updatedSegmentDetails = yield call(
        segmentDataApi.getSegmentDetails,
        segmentType,
        segmentDetails?.segmentCode || segmentCode,
        response?.version
      );
      yield put(receiveSegmentdetails({
        segmentDetails: updatedSegmentDetails,
        segmentCode: segmentDetails?.segmentCode || segmentCode
      }));
    } catch (fetchError) {
      // Failed to fetch segment details after update
    }

    try {
      const versionData = yield call(
        segmentDataApi.getSegmentVersions,
        segmentType,
        segmentDetails?.segmentCode || segmentCode
      );
      yield put(receiveSegmentVersions(versionData));
    } catch (fetchError) {
      // Failed to fetch segment versions after update
    }

    yield put(receiveSegmentDetailsUpdatetStatus());
    if (selectedVendorCode) {
      yield put(syncSelectedVendorInFilter(selectedVendorCode));
    }
  } catch (e) {
    const errorMessage = e.response?.data ? e.response?.data : UNABLE_TO_SAVE_SEGMENT_DETAILS;
    yield put(receiveErrorInSegmentDetailsUpdate(errorMessage));
  }
}

export function* deleteSegmentDetail({ data }) {
  const { segmentType, segmentCode, segmentVersion } = data;
  try {
    yield call(segmentDataApi.deleteSegmentDetail, segmentType, segmentCode, segmentVersion);

    try {
      const segmentDetails = yield call(segmentDataApi.getSegmentDetails, segmentType, segmentCode, segmentVersion);
      yield put(receiveSegmentdetails({ segmentDetails, segmentCode }));
    } catch (e) {
      // silent
    }

    yield put(receiveSegmentDetailsDeleteStatus());
  } catch (e) {
    const errorMessage = e.response?.data ? e.response?.data : UNABLE_TO_DELETE_SEGMENT_DETAILS;
    yield put(receiveErrorInSegmentDetailsDeletion(errorMessage));
  }
}

export function* fetchSegmentVersions({ data }) {
  const { segmentType, segmentCode } = data;
  try {
    const versionData = yield call(segmentDataApi.getSegmentVersions, segmentType, segmentCode);
    yield put(receiveSegmentVersions(versionData));
  } catch (error) {
    yield put(segmentVersionsError('Unable to load segment versions'));
  }
}

/**
 * Starts fetching segment details related actions.
 */
export function* segmentDataSaga() {
  yield takeEvery(FETCH_SEGMENT_CODES, getSegmentCodes);
  yield takeEvery(FETCH_SEGMENT_DETAILS, fetchSegmentDetails);
  yield takeEvery(SAVE_SEGMENT_DETAILS, saveSegmentDetail);
  yield takeEvery(UPDATE_SEGMENT_DETAILS, updateSegmentDetail);
  yield takeEvery(DELETE_SEGMENT_DETAILS, deleteSegmentDetail);
  yield takeEvery(FETCH_SEGMENT_VERSIONS, fetchSegmentVersions);
  yield takeEvery(FETCH_SEGMENT_VENDOR_MAPPING, fetchSegmentVendorMapping);
}
